import { action, makeObservable, observable } from "mobx";
import { MethodRequest, PaymentProvider, PaymentVerifyStatus } from "../../constants";
import { API } from "../../core";
import { PaymentProdamusCheckResponse, StripeCheckSessionResponse } from "../../response";

export class PaymentStore {
	@observable verifyStatus: PaymentVerifyStatus = PaymentVerifyStatus.Idle;
	@observable provider: PaymentProvider = PaymentProvider.Unknown;
	@observable errorMessage = "";

	constructor() {
		makeObservable(this);
	}

	@action.bound
	setVerifyStatus(value: PaymentVerifyStatus) {
		this.verifyStatus = value;
	}

	@action.bound
	setProvider(value: PaymentProvider) {
		this.provider = value;
	}

	@action.bound
	setErrorMessage(value = "") {
		this.errorMessage = value;
	}

	getProviderBySearchParams(searchParams: URLSearchParams): PaymentProvider {
		if (searchParams.get("_payform_id") || searchParams.get("_payform_order_id")) {
			return PaymentProvider.Prodamus;
		}
		if (searchParams.get("session_id")) {
			return PaymentProvider.Stripe;
		}
		return PaymentProvider.Unknown;
	}

	@action.bound
	async verifyOrderSuccess(searchParams: URLSearchParams, authStore: any) {
		this.setVerifyStatus(PaymentVerifyStatus.Verifying);
		this.setErrorMessage("");

		const provider = this.getProviderBySearchParams(searchParams);

		this.setProvider(provider);

		try {
			if (provider === PaymentProvider.Prodamus) {
				await this.verifyProdamusReturn(searchParams, authStore);
				return;
			}

			if (provider === PaymentProvider.Stripe) {
				await this.verifyStripeReturn(searchParams, authStore);
				return;
			}

			this.setErrorMessage("Payment provider was not detected.");
			this.setVerifyStatus(PaymentVerifyStatus.Failed);
		} catch (error) {
			console.error("PaymentStore.verifyOrderSuccess error:", error);

			this.setErrorMessage("Payment verification failed.");
			this.setVerifyStatus(PaymentVerifyStatus.Failed);
		}
	}

	@action.bound
	async verifyProdamusReturn(searchParams: URLSearchParams, authStore: any) {
		const result = await API.request<PaymentProdamusCheckResponse>("payments/prodamus/check-return", {
			method: MethodRequest.Post,
			body: API.getFormData({
				_payform_status: searchParams.get("_payform_status"),
				_payform_id: searchParams.get("_payform_id"),
				_payform_order_id: searchParams.get("_payform_order_id"),
				_payform_sign: searchParams.get("_payform_sign"),
			}),
		});

		if (result.success) {
			await authStore.check();
			this.setVerifyStatus(PaymentVerifyStatus.Success);
			return;
		}

		this.setErrorMessage(result.message || "Payment is still being processed.");
		this.setVerifyStatus(PaymentVerifyStatus.Pending);
	}

	@action.bound
	async verifyStripeReturn(searchParams: URLSearchParams, authStore: any) {
		const sessionId = searchParams.get("session_id");

		if (!sessionId) {
			this.setErrorMessage("Stripe session was not found.");
			this.setVerifyStatus(PaymentVerifyStatus.Failed);
			return;
		}

		const query = new URLSearchParams({
			session_id: sessionId,
		}).toString();

		const result = await API.request<StripeCheckSessionResponse>(`payments/check-session?${query}`, {
			method: MethodRequest.Get,
		});

		if (result.payment_status === "paid" || result.order_status === "paid") {
			await authStore.check();
			this.setVerifyStatus(PaymentVerifyStatus.Success);
			return;
		}

		this.setErrorMessage("Payment is still being confirmed.");
		this.setVerifyStatus(PaymentVerifyStatus.Pending);
	}

	@action.bound
	reset() {
		this.verifyStatus = PaymentVerifyStatus.Idle;
		this.provider = PaymentProvider.Unknown;
		this.errorMessage = "";
	}
}
